import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { user as userApi, APIError } from '../api/client'
import { useAppContext } from '../context/AppContext'
import { AuthLayout } from '../components/layout/AuthLayout'
import { AppBrandingHeader } from '../components/auth/AppBrandingHeader'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
} from '../components/ui'

export function CompleteProfilePage() {
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('return_to') || sessionStorage.getItem('u_auth_return_to')
  const { appInfo } = useAppContext()

  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [displayNameError, setDisplayNameError] = useState<string | null>(null)

  const validateUsername = (value: string): boolean => {
    if (!value) {
      setUsernameError('Username is required')
      return false
    }
    if (value.length < 3) {
      setUsernameError('Username must be at least 3 characters')
      return false
    }
    if (value.length > 18) {
      setUsernameError('Username must be at most 18 characters')
      return false
    }
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      setUsernameError('Username can only contain letters, numbers, and underscores')
      return false
    }
    setUsernameError(null)
    return true
  }

  const validateDisplayName = (value: string): boolean => {
    if (!value.trim()) {
      setDisplayNameError('Display name is required')
      return false
    }
    if (value.length > 100) {
      setDisplayNameError('Display name must be at most 100 characters')
      return false
    }
    setDisplayNameError(null)
    return true
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    const isUsernameValid = validateUsername(username)
    const isDisplayNameValid = validateDisplayName(displayName)

    if (!isUsernameValid || !isDisplayNameValid) {
      return
    }

    setLoading(true)

    try {
      // Set username
      await userApi.setUsername(username)

      // Update display name
      await userApi.updateProfile({ display_name: displayName.trim() })

      // Redirect to return_to or account
      const redirectUrl = returnTo || import.meta.env.VITE_ACCOUNT_URL || '/'
      window.location.href = redirectUrl
    } catch (err) {
      if (err instanceof APIError) {
        if (err.message.toLowerCase().includes('username')) {
          setUsernameError(err.message)
        } else {
          setError(err.message)
        }
      } else {
        setError('An unexpected error occurred')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      {/* App branding when in OAuth flow */}
      <AppBrandingHeader
        appInfo={appInfo}
        subtitle="Complete your profile to continue to"
        showDefaultBranding={false}
      />

      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
            <svg
              className="h-6 w-6 text-primary-600 dark:text-primary-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
          <CardTitle>Complete your profile</CardTitle>
          <CardDescription>
            Set up your username and display name to get started
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Input
              label="Username"
              type="text"
              name="username"
              value={username}
              onChange={(e) => {
                const value = e.target.value.toLowerCase()
                setUsername(value)
                if (usernameError) validateUsername(value)
              }}
              onBlur={() => validateUsername(username)}
              placeholder="johndoe"
              required
              autoComplete="username"
              autoFocus
              error={usernameError || undefined}
              hint={
                !usernameError
                  ? 'Letters, numbers, and underscores only. Cannot be changed later.'
                  : undefined
              }
            />

            <Input
              label="Display name"
              type="text"
              name="displayName"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value)
                if (displayNameError) validateDisplayName(e.target.value)
              }}
              onBlur={() => validateDisplayName(displayName)}
              placeholder="John Doe"
              required
              autoComplete="name"
              error={displayNameError || undefined}
              hint={!displayNameError ? 'Shown on your profile and to other users' : undefined}
            />

            <Button type="submit" fullWidth loading={loading} className="mt-6">
              Continue
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
